import { Phase } from "./phase";
import { Pool } from "interfaces/pool";
import { CONTEST_ENTRY_STATUS, CONTEST_STATE } from "utils/constants.ts";
import { AdBanner } from "interfaces/ad-banner.ts";
import { BetTypeName, betTypeExacta, betTypeTrifecta } from "interfaces/bet-type-name";

export const CONTEST_TYPE_PRO = "pro" as const;
export const CONTEST_TYPE_BASIC = "basic" as const;

export type ContestType = typeof CONTEST_TYPE_PRO | typeof CONTEST_TYPE_BASIC;

export const isBasicContest = (c: { type?: ContestType } | undefined): boolean =>
  c?.type === CONTEST_TYPE_BASIC;

// A Basic contest where every pick costs 1 whatever its bet type, each pool
// takes one pick per entry, and the entry is valid once it has picked every pool.
export const isPickPerPoolContest = (
  c: { type?: ContestType; settings?: { pick_per_pool?: boolean } } | undefined
): boolean => isBasicContest(c) && c?.settings?.pick_per_pool === true;

// Mirrors the backend's `len(split(";"))` rule for Basic contests.
export const picksCostForPoolType = (poolType: BetTypeName): number => {
  if (poolType === betTypeTrifecta) return 3;
  if (poolType === betTypeExacta) return 2;
  return 1;
};

export const picksCostForContestPick = (
  contest: { type?: ContestType; settings?: { pick_per_pool?: boolean } } | undefined,
  poolType: BetTypeName
): number => (isPickPerPoolContest(contest) ? 1 : picksCostForPoolType(poolType));

// Picks remaining for a Basic contest entry = total allotment minus picks
// already made, derived from fields already on the entry. For Basic contests
// the backend sets `unique_contestants_required` to the pick allotment (the
// initial reward) and `unique_contestants_picked` to picks consumed — these
// are equal to the weighted pick count because Basic forbids duplicate
// contestants (an exacta adds 2, a trifecta 3, to both). This is immune to
// pool winnings being credited back into the entry wallet, unlike the wallet
// balance. Meaningless for Pro contests; the backend enforces the real limit.
export const remainingPicksForEntry = (
  entry: { unique_contestants_required?: number; unique_contestants_picked?: number } | undefined
): number => {
  const total = entry?.unique_contestants_required ?? 0;
  const made = entry?.unique_contestants_picked ?? 0;
  return Math.max(0, total - made);
};

// True when contest-scoped betting is no longer allowed. Basic-only: Pro
// contests rely on the existing pool-status gates and are never blocked here.
export const isContestBettingBlocked = (
  contest: { type?: ContestType; state?: CONTEST_STATE } | undefined,
  remainingPicks: number | undefined
): boolean => {
  if (!contest || !isBasicContest(contest)) return false;
  if (contest.state === CONTEST_STATE.LIVE || contest.state === CONTEST_STATE.SETTLED) {
    return true;
  }
  if (remainingPicks !== undefined && remainingPicks <= 0) {
    return true;
  }
  return false;
};

export interface ContestGroup {
  contest_id: string;
  group_id: string;
  weight: number;
  entry_fee: number;
}

export interface Contest {
  end_at: number;
  headline: string;
  id: string;
  state: CONTEST_STATE;
  is_active: boolean; // default on the leaderboard
  is_deleted: boolean;
  name: string;
  phases: Phase[] | undefined;
  settings: { logo_url?: string, ad_banner?: AdBanner, pick_per_pool?: boolean };
  start_at: number;
  contest_terms: string | undefined;
  type?: ContestType;
  initial_reward_amount?: number;
  max_entries: number | null;
  max_entries_per_user: number | null;
  total_entries: number;
  total_prize_value: string | null;
  pools?: Pool[];
  contest_groups: ContestGroup[];
}

export interface ContestEntry {
  id: string;
  status: CONTEST_ENTRY_STATUS;
  created_at: string;
  unique_contestants_picked: number;
  unique_contestants_required: number;
  total_earnings?: number;
}

export interface JoinedContest {
  contest_id: string;
  num_entries: number;
  entries: ContestEntry[];
}
